import React from 'react';
import PropTypes from "prop-types";
import {ResponsiveBar} from "@nivo/bar";
import { BasicTooltip, useTooltip } from '@nivo/tooltip';
import {format} from "d3-format";
import {COLOR_PALETTE, formatAmount} from "../misc/Shared";
import {isEmpty} from "lodash";
import { line } from "d3-shape";

const propTypes = {
	graph: PropTypes.object.isRequired
};

const defaultProps = {};

class Graph extends React.Component {
	constructor(props) {
		super(props);

		this.targetLayer = this.targetLayer.bind(this);

		this.state = {
			graph: this.props.graph,
			barChart: [],
			barChartColumns: [],
			barChartKeys: [],
			hasTarget: false,
			highestValue: 'auto',
			printing: false
		};
	}

	componentDidMount() {
		this.convertDataToChart();
		window.onbeforeprint = () => this.setState({printing: true});
		window.onafterprint = () => this.setState({printing: false});
	}

	convertDataToChart() {
		let data = this.props.graph;
		let barChart = data.columns.sort((a,b) => a.displayOrder - b.displayOrder).map(column => {
			let newBarChart = { indexBy: column.title };
			let columnData = data.data.filter(item => item.columnTitle === column.title);
			columnData.forEach(data => {
				newBarChart[data.rowTitle] = data.amount;
			});
			return newBarChart;
		});
		let barChartColumns = data.columns.map(column => column.title);
		let barChartKeys = data.rows.map(row => row.title);
		let highestValue = Math.max(...data.data.map(i => i.amount));

		let hasTarget = false;
		const index = barChartKeys.indexOf("Target");
		if (index > -1) {
			barChartKeys.splice(index, 1);
			hasTarget = true;
		}

		this.setState({
			barChart: barChart,
			barChartColumns: barChartColumns,
			barChartKeys: barChartKeys,
			hasTarget: hasTarget,
			highestValue: isNaN(highestValue) ? 'auto' : highestValue * 1.1,
		});
	}

	targetLayer({ bars, xScale, yScale }){
		if(this.state.hasTarget){
			const filterBars = bars.map(bar => ({value: bar.data.data.Target, column: bar.data.data.indexBy, width: bar.width, key: bar.key}));
			const distinct = [];
			const map = new Map();
			for (const item of filterBars) {
				if(!map.has(item.column)){
					map.set(item.column, true);
					distinct.push({
						value: item.value || 0,
						column: item.column,
						width: item.width,
						key: item.key
					});
				}
			}


			const lineGenerator = line()
				.x(bar => xScale(bar.column) + bar.width / 2)
				.y(bar => yScale(bar.value));

			const { showTooltipFromEvent, hideTooltip } = useTooltip();

			return (
				<>
					<path
						d={lineGenerator(distinct)}
						fill="none"
						stroke={"rgba(245, 142, 39, .85)"}
						strokeWidth="3"
						style={{ pointerEvents: "none" }}
					/>
					{distinct.map(bar => (
						<circle
							key={bar.key}
							cx={xScale(bar.column) + bar.width / 2}
							cy={yScale(bar.value)}
							r={6}
							fill="white"
							stroke={"rgba(245, 142, 39, 1)"}
							strokeWidth="2"
							onMouseEnter={e => showTooltipFromEvent(<BasicTooltip id="Target" value={formatAmount(bar.value, 2, this.state.graph.amountPrefix, this.state.graph.amountSuffix)} />, e)}
							onMouseLeave={() => hideTooltip()}
							onMouseMove={e => showTooltipFromEvent(<BasicTooltip id="Target" value={formatAmount(bar.value, 2, this.state.graph.amountPrefix, this.state.graph.amountSuffix)} />, e)}
						/>
					))}
				</>
			);
		}

		return <></>;
	}

	render() {
		return (
			<div>
				<h3>{this.state.graph.title}</h3>
				<div dangerouslySetInnerHTML={{__html: this.state.graph.description}}/>
				{this.state.hasTarget && <div className="legend float-right">
						<div className="legend--item">
							<div className="legend--item--symbol background--color-orange"/>
							<span>Target</span>
						</div>
				</div>}
				{!isEmpty(this.state.barChart) && <div className="bar-graph" style={this.state.printing ? {width: '8.5in'} : undefined}>
					<ResponsiveBar
						data={this.state.barChart}
						keys={this.state.barChartKeys}
						indexBy="indexBy"
						margin={{
							"top": 50,
							"right": 20,
							"bottom": 100,
							"left": 70
						}}
						padding={0.3}
						borderColor="inherit:darker(1.6)"
						axisBottom={{
							"orient": "bottom",
							"tickSize": 5,
							"tickPadding": 5,
							"legend": this.state.graph.axisTitleX + ` ${this.state.graph.unitTypeX ? `(${this.state.graph.unitTypeX})` : ''}`,
							"legendPosition": "middle",
							"legendOffset": 32
						}}
						axisLeft={{
							"orient": "left",
							"tickSize": 5,
							"tickPadding": 5,
							"tickRotation": 0,
							"legend": this.state.graph.axisTitleY + ` ${this.state.graph.unitTypeY ? `(${this.state.graph.unitTypeY})` : ''}`,
							"legendPosition": "middle",
							"legendOffset": -60,
							"format": v =>  v > 100 ? format(".2s")(v).replace(/G/,"B") : v
						}}
						labelSkipWidth={12}
						labelSkipHeight={12}
						labelTextColor="inherit:darker(1.6)"
						animate={true}
						motionStiffness={90}
						motionDamping={15}
						enableLabel={false}
						colors={COLOR_PALETTE}
						maxValue={this.state.highestValue}
						tooltip={(data) => {
							return <BasicTooltip id={data.id} value={formatAmount(data.value, 2, this.state.graph.amountPrefix, this.state.graph.amountSuffix)}/>;
						}}
						layers={['grid', 'axes', 'bars', this.targetLayer, 'markers', 'legends', 'annotations']}
					/>
				</div>}
			</div>
		);
	}
}

Graph.propTypes = propTypes;
Graph.defaultProps = defaultProps;

export default Graph;
